import React, { useState, useCallback, useEffect } from 'react'
import {
  Button,
  TextField,
  TextArea,
  Dropdown,
  Heading,
  Text,
  Toast,
  Box,
  Flex,
} from '@vibe/core'
import { Add, Remove, Check, Warning } from '@vibe/icons'
import { getErrorMessage } from '../../utils/errorHandling'
import type { JobDefinitionFormData } from '../../types'

// Form validation rules
const VALIDATION_RULES = {
  name: { min: 3, max: 100 },
  puesto: { min: 3, max: 100 },
  funcion: { min: 10, max: 500 },
  ocupabilidad: { min: 1, max: 100 },
  lenguaje: { min: 2, max: 50 },
  detalle: { min: 3, max: 200 },
  conocimiento: { min: 2, max: 100 },
  requisito: { min: 10, max: 500 },
  experiencia: { min: 0, max: 50 },
  meses: { min: 0, max: 11 },
}

// Dropdown options
const NIVEL_IDIOMA_OPTIONS = [
  { value: 'Básico', label: 'Básico' },
  { value: 'Intermedio', label: 'Intermedio' },
  { value: 'Avanzado', label: 'Avanzado' },
  { value: 'Nativo', label: 'Nativo' },
]

const NIVEL_ESTUDIOS_OPTIONS = [
  { value: 'primaria_secundaria', label: 'Primaria/Secundaria' },
  { value: 'tecnico_basico', label: 'Técnico Básico' },
  { value: 'tecnico_especializado', label: 'Técnico Especializado' },
  { value: 'universitario', label: 'Universitario' },
  { value: 'titulado', label: 'Titulado' },
  { value: 'diplomado', label: 'Diplomado/Especialización' },
  { value: 'maestria', label: 'Maestría' },
  { value: 'doctorado', label: 'Doctorado' },
]

interface FormErrors {
  [key: string]: string
}

interface JobDefinitionFormProps {
  onSubmit: (data: JobDefinitionFormData) => void
  onCancel: () => void
  initialData?: Partial<JobDefinitionFormData>
  mode: 'create' | 'edit'
}

export const JobDefinitionForm: React.FC<JobDefinitionFormProps> = ({
  onSubmit,
  onCancel,
  initialData,
  mode,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState<FormErrors>({})
  const [hasAttemptedSubmission, setHasAttemptedSubmission] = useState(false)
  const [toast, setToast] = useState<{
    message: string
    type: 'positive' | 'negative'
  } | null>(null)

  // Form data state
  const [formData, setFormData] = useState<JobDefinitionFormData>({
    name: initialData?.name || '',
    puesto: initialData?.puesto || '',
    funciones: initialData?.funciones || [{ funcion: '', ocupabilidad: 100 }],
    idiomas: initialData?.idiomas || [{ lenguaje: '', nivel: '' }],
    estudios: initialData?.estudios || [{ nivel: '', detalle: '' }],
    conocimientos: initialData?.conocimientos || [''],
    requisitos_indispensables: initialData?.requisitos_indispensables || [''],
  })

  // Calculate total ocupabilidad
  const totalOcupabilidad = formData.funciones.reduce(
    (total, funcion) => total + (funcion.ocupabilidad || 0),
    0
  )

  // Validation functions
  const validateField = useCallback((field: string, value: any): string => {
    if (field === 'name') {
      if (!value?.trim()) return 'El nombre de la definición es obligatorio'
      if (value.length < VALIDATION_RULES.name.min)
        return `Mínimo ${VALIDATION_RULES.name.min} caracteres`
      if (value.length > VALIDATION_RULES.name.max)
        return `Máximo ${VALIDATION_RULES.name.max} caracteres`
    }

    if (field === 'puesto') {
      if (!value?.trim()) return 'El nombre de la posición es obligatorio'
      if (value.length < VALIDATION_RULES.puesto.min)
        return `Mínimo ${VALIDATION_RULES.puesto.min} caracteres`
      if (value.length > VALIDATION_RULES.puesto.max)
        return `Máximo ${VALIDATION_RULES.puesto.max} caracteres`
    }

    if (field.includes('funcion')) {
      if (!value?.trim()) return 'La función no puede estar vacía'
      if (value.length < VALIDATION_RULES.funcion.min)
        return `Mínimo ${VALIDATION_RULES.funcion.min} caracteres`
      if (value.length > VALIDATION_RULES.funcion.max)
        return `Máximo ${VALIDATION_RULES.funcion.max} caracteres`
    }

    if (field.includes('ocupabilidad')) {
      const num = Number(value)
      if (isNaN(num) || num < VALIDATION_RULES.ocupabilidad.min)
        return `Mínimo ${VALIDATION_RULES.ocupabilidad.min}%`
      if (num > VALIDATION_RULES.ocupabilidad.max)
        return `Máximo ${VALIDATION_RULES.ocupabilidad.max}%`
    }

    return ''
  }, [])

  // Form handlers
  const updateFormData = useCallback(
    (updates: Partial<JobDefinitionFormData>) => {
      setFormData(prev => ({ ...prev, ...updates }))
    },
    []
  )

  const addArrayItem = useCallback(
    (field: keyof JobDefinitionFormData, defaultItem: any) => {
      setFormData(prev => ({
        ...prev,
        [field]: [...(prev[field] as any[]), defaultItem],
      }))
    },
    []
  )

  const removeArrayItem = useCallback(
    (field: keyof JobDefinitionFormData, index: number) => {
      setFormData(prev => ({
        ...prev,
        [field]: (prev[field] as any[]).filter((_, i) => i !== index),
      }))
    },
    []
  )

  const updateArrayItem = useCallback(
    (field: keyof JobDefinitionFormData, index: number, updates: any) => {
      setFormData(prev => ({
        ...prev,
        [field]: (prev[field] as any[]).map((item, i) =>
          i === index ? { ...item, ...updates } : item
        ),
      }))
    },
    []
  )

  // Validate entire form
  const validateForm = useCallback((): boolean => {
    const allErrors: FormErrors = {}

    // Validate name
    const nameError = validateField('name', formData.name)
    if (nameError) allErrors.name = nameError

    // Validate puesto
    const puestoError = validateField('puesto', formData.puesto)
    if (puestoError) allErrors.puesto = puestoError

    // Validate functions
    formData.funciones.forEach((funcion, index) => {
      const funcionError = validateField('funcion', funcion.funcion)
      if (funcionError) allErrors[`funciones.${index}.funcion`] = funcionError

      const ocupabilidadError = validateField(
        'ocupabilidad',
        funcion.ocupabilidad
      )
      if (ocupabilidadError)
        allErrors[`funciones.${index}.ocupabilidad`] = ocupabilidadError
    })

    if (totalOcupabilidad !== 100) {
      allErrors.ocupabilidad_total = `La ocupabilidad total debe ser 100%, actualmente es ${totalOcupabilidad}%`
    }

    // Validate profile data (optional fields)
    formData.idiomas.forEach((idioma, index) => {
      const hasContent = idioma.lenguaje.trim() || idioma.nivel.trim()
      if (hasContent) {
        if (!idioma.lenguaje.trim())
          allErrors[`idiomas.${index}.lenguaje`] = 'El idioma es obligatorio'
        if (!idioma.nivel.trim())
          allErrors[`idiomas.${index}.nivel`] = 'El nivel es obligatorio'
      }
    })

    formData.estudios.forEach((estudio, index) => {
      const hasContent = estudio.nivel.trim() || estudio.detalle.trim()
      if (hasContent) {
        if (!estudio.nivel.trim())
          allErrors[`estudios.${index}.nivel`] = 'El nivel es obligatorio'
        if (!estudio.detalle.trim())
          allErrors[`estudios.${index}.detalle`] = 'El detalle es obligatorio'
      }
    })

    // Validate requirements (optional)
    formData.requisitos_indispensables.forEach((requisito, index) => {
      if (
        requisito.trim() &&
        requisito.length < VALIDATION_RULES.requisito.min
      ) {
        allErrors[`requisitos.${index}`] =
          `Mínimo ${VALIDATION_RULES.requisito.min} caracteres`
      }
    })

    setErrors(allErrors)
    return Object.keys(allErrors).length === 0
  }, [formData, totalOcupabilidad, validateField])

  // Form submission handler
  const handleSubmit = useCallback(async () => {
    setHasAttemptedSubmission(true)

    if (!validateForm()) {
      setToast({
        message: 'Por favor corrige los errores antes de continuar',
        type: 'negative',
      })
      return
    }

    setIsSubmitting(true)

    try {
      // Clean up form data before submission
      const cleanedData: JobDefinitionFormData = {
        ...formData,
        idiomas: formData.idiomas.filter(
          idioma => idioma.lenguaje.trim() && idioma.nivel.trim()
        ),
        estudios: formData.estudios.filter(
          estudio => estudio.nivel.trim() && estudio.detalle.trim()
        ),
        conocimientos: formData.conocimientos.filter(c => c.trim()),
        requisitos_indispensables: formData.requisitos_indispensables.filter(
          r => r.trim()
        ),
      }

      await onSubmit(cleanedData)
    } catch (error) {
      console.error('Error submitting job definition:', error)
      setToast({
        message: getErrorMessage(error),
        type: 'negative',
      })
    } finally {
      setIsSubmitting(false)
    }
  }, [formData, validateForm, onSubmit])

  // Auto-hide toast
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 5000)
      return () => clearTimeout(timer)
    }
  }, [toast])

  return (
    <Box className="max-w-4xl mx-auto">
      {/* Toast notifications */}
      {toast && (
        <Toast
          open={true}
          onClose={() => setToast(null)}
          className="fixed top-4 right-4 z-50"
          type={toast.type}
        >
          {toast.message}
        </Toast>
      )}

      <Box className="space-y-8">
        {/* Basic Information Section */}
        <Box className="space-y-6">
          <Heading type="h3" className="mb-4 pb-2 border-b">
            Información Básica
          </Heading>

          {/* Definition name */}
          <Box>
            <TextField
              title="Nombre de la definición *"
              placeholder="Ej: Desarrollador Senior Frontend - Estándar"
              value={formData.name}
              onChange={value => updateFormData({ name: value })}
              validation={{
                status:
                  hasAttemptedSubmission && errors.name ? 'error' : undefined,
                text: errors.name,
              }}
              required
            />
          </Box>

          {/* Position name */}
          <Box>
            <TextField
              title="Nombre de la posición *"
              placeholder="Ej: Desarrollador Senior Frontend"
              value={formData.puesto}
              onChange={value => updateFormData({ puesto: value })}
              validation={{
                status:
                  hasAttemptedSubmission && errors.puesto ? 'error' : undefined,
                text: errors.puesto,
              }}
              required
            />
          </Box>

          {/* Functions */}
          <Box>
            <Text className="text-lg font-semibold mb-4">
              Funciones del puesto *
            </Text>
            <Text color="secondary" className="mb-4">
              Define las funciones principales y su porcentaje de ocupabilidad
              (debe sumar 100%)
            </Text>

            {formData.funciones.map((funcion, index) => (
              <Box key={index} className="mb-4 p-4 border rounded-lg">
                <Flex gap="medium" align="start">
                  <Box className="flex-1">
                    <TextArea
                      title={`Función ${index + 1}`}
                      placeholder="Describe la función principal..."
                      value={funcion.funcion}
                      onChange={(
                        event: React.ChangeEvent<HTMLTextAreaElement>
                      ) =>
                        updateArrayItem('funciones', index, {
                          funcion: event.target.value,
                        })
                      }
                      rows={3}
                    />
                    {hasAttemptedSubmission &&
                      errors[`funciones.${index}.funcion`] && (
                        <Text className="text-red-600 text-sm mt-1">
                          {errors[`funciones.${index}.funcion`]}
                        </Text>
                      )}
                  </Box>
                  <Box className="w-32">
                    <TextField
                      title="Ocupabilidad %"
                      type="number"
                      value={String(funcion.ocupabilidad)}
                      onChange={value =>
                        updateArrayItem('funciones', index, {
                          ocupabilidad: Number(value),
                        })
                      }
                      validation={{
                        status:
                          hasAttemptedSubmission &&
                          errors[`funciones.${index}.ocupabilidad`]
                            ? 'error'
                            : undefined,
                        text: errors[`funciones.${index}.ocupabilidad`],
                      }}
                    />
                  </Box>
                  {formData.funciones.length > 1 && (
                    <Button
                      kind="tertiary"
                      size="small"
                      onClick={() => removeArrayItem('funciones', index)}
                      aria-label="Eliminar función"
                    >
                      <Remove />
                    </Button>
                  )}
                </Flex>
              </Box>
            ))}

            <Flex justify="space-between" align="center" className="mt-4">
              <Button
                kind="tertiary"
                size="small"
                onClick={() =>
                  addArrayItem('funciones', { funcion: '', ocupabilidad: 0 })
                }
              >
                <Add /> Agregar Función
              </Button>
              <Text
                className={`font-medium ${
                  totalOcupabilidad === 100 ? 'text-green-600' : 'text-red-600'
                }`}
              >
                Total: {totalOcupabilidad}%
                {totalOcupabilidad === 100 && <Check className="ml-1 inline" />}
              </Text>
            </Flex>

            {hasAttemptedSubmission && errors.ocupabilidad_total && (
              <Text className="mt-2 text-red-600">
                <Warning className="inline mr-1" />
                {errors.ocupabilidad_total}
              </Text>
            )}
          </Box>
        </Box>

        {/* Profile Section */}
        <Box className="space-y-6 dropdown-overflow-visible">
          <Heading type="h3" className="mb-4 pb-2 border-b">
            Perfil del Puesto
          </Heading>

          {/* Languages */}
          <Box className="dropdown-overflow-visible">
            <Text className="text-lg font-semibold mb-4">Idiomas</Text>
            <Text color="secondary" className="mb-4">
              Especifica los idiomas requeridos y su nivel de dominio
            </Text>

            {formData.idiomas.map((idioma, index) => (
              <Flex key={index} gap="medium" align="start" className="mb-4">
                <Box className="flex-1">
                  <TextField
                    title="Idioma"
                    placeholder="Ej: Inglés"
                    value={idioma.lenguaje}
                    onChange={value =>
                      updateArrayItem('idiomas', index, { lenguaje: value })
                    }
                    validation={{
                      status:
                        hasAttemptedSubmission &&
                        errors[`idiomas.${index}.lenguaje`]
                          ? 'error'
                          : undefined,
                      text: errors[`idiomas.${index}.lenguaje`],
                    }}
                  />
                </Box>
                <Box className="flex-1 dropdown-overflow-visible">
                  <Text className="mb-2 font-medium">Nivel</Text>
                  <Dropdown
                    placeholder="Seleccionar nivel"
                    size="small"
                    options={NIVEL_IDIOMA_OPTIONS}
                    value={
                      NIVEL_IDIOMA_OPTIONS.find(
                        opt => opt.value === idioma.nivel
                      ) || null
                    }
                    onChange={(selectedOption: any) => {
                      const value = selectedOption?.value || ''
                      updateArrayItem('idiomas', index, { nivel: value })
                    }}
                  />
                  {hasAttemptedSubmission &&
                    errors[`idiomas.${index}.nivel`] && (
                      <Text className="text-red-600 text-sm mt-1">
                        {errors[`idiomas.${index}.nivel`]}
                      </Text>
                    )}
                </Box>
                {formData.idiomas.length > 1 && (
                  <Button
                    kind="tertiary"
                    size="small"
                    onClick={() => removeArrayItem('idiomas', index)}
                    aria-label="Eliminar idioma"
                  >
                    <Remove />
                  </Button>
                )}
              </Flex>
            ))}

            <Button
              kind="tertiary"
              size="small"
              onClick={() =>
                addArrayItem('idiomas', { lenguaje: '', nivel: '' })
              }
            >
              <Add /> Agregar Idioma
            </Button>
          </Box>

          {/* Education */}
          <Box className="dropdown-overflow-visible">
            <Text className="text-lg font-semibold mb-4">Estudios</Text>
            <Text color="secondary" className="mb-4">
              Define los niveles de estudio requeridos
            </Text>

            {formData.estudios.map((estudio, index) => (
              <Flex key={index} gap="medium" align="start" className="mb-4">
                <Box className="flex-1 dropdown-overflow-visible">
                  <Text className="mb-2 font-medium">Nivel de estudios</Text>
                  <Dropdown
                    placeholder="Seleccionar nivel"
                    size="small"
                    options={NIVEL_ESTUDIOS_OPTIONS}
                    value={
                      NIVEL_ESTUDIOS_OPTIONS.find(
                        opt => opt.value === estudio.nivel
                      ) || null
                    }
                    onChange={(selectedOption: any) => {
                      const value = selectedOption?.value || ''
                      updateArrayItem('estudios', index, { nivel: value })
                    }}
                  />
                  {hasAttemptedSubmission &&
                    errors[`estudios.${index}.nivel`] && (
                      <Text className="text-red-600 text-sm mt-1">
                        {errors[`estudios.${index}.nivel`]}
                      </Text>
                    )}
                </Box>
                <Box className="flex-1">
                  <TextField
                    title="Detalle"
                    placeholder="Ej: Ingeniería en Sistemas"
                    value={estudio.detalle}
                    onChange={value =>
                      updateArrayItem('estudios', index, { detalle: value })
                    }
                    validation={{
                      status:
                        hasAttemptedSubmission &&
                        errors[`estudios.${index}.detalle`]
                          ? 'error'
                          : undefined,
                      text: errors[`estudios.${index}.detalle`],
                    }}
                  />
                </Box>
                {formData.estudios.length > 1 && (
                  <Button
                    kind="tertiary"
                    size="small"
                    onClick={() => removeArrayItem('estudios', index)}
                    aria-label="Eliminar estudio"
                  >
                    <Remove />
                  </Button>
                )}
              </Flex>
            ))}

            <Button
              kind="tertiary"
              size="small"
              onClick={() =>
                addArrayItem('estudios', { nivel: '', detalle: '' })
              }
            >
              <Add /> Agregar Estudio
            </Button>
          </Box>

          {/* Knowledge */}
          <Box>
            <Text className="text-lg font-semibold mb-4">Conocimientos</Text>
            <Text color="secondary" className="mb-4">
              Lista los conocimientos técnicos y habilidades requeridas
            </Text>

            {formData.conocimientos.map((conocimiento, index) => (
              <Flex key={index} gap="medium" align="center" className="mb-4">
                <Box className="flex-1">
                  <TextField
                    placeholder="Ej: React, TypeScript, Node.js"
                    value={conocimiento}
                    onChange={value => {
                      const updatedConocimientos = [...formData.conocimientos]
                      updatedConocimientos[index] = value
                      updateFormData({ conocimientos: updatedConocimientos })
                    }}
                  />
                </Box>
                {formData.conocimientos.length > 1 && (
                  <Button
                    kind="tertiary"
                    size="small"
                    onClick={() => removeArrayItem('conocimientos', index)}
                    aria-label="Eliminar conocimiento"
                  >
                    <Remove />
                  </Button>
                )}
              </Flex>
            ))}

            <Button
              kind="tertiary"
              size="small"
              onClick={() => addArrayItem('conocimientos', '')}
            >
              <Add /> Agregar Conocimiento
            </Button>
          </Box>
        </Box>

        {/* Requirements Section */}
        <Box className="space-y-6">
          <Heading type="h3" className="mb-4 pb-2 border-b">
            Requisitos Indispensables
          </Heading>

          <Text color="secondary" className="mb-4">
            Define los requisitos que son absolutamente necesarios para el
            puesto
          </Text>

          {formData.requisitos_indispensables.map((requisito, index) => (
            <Flex key={index} gap="medium" align="start" className="mb-4">
              <Box className="flex-1">
                <TextArea
                  placeholder="Ej: Experiencia mínima de 3 años en desarrollo frontend..."
                  value={requisito}
                  onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
                    const updatedRequisitos = [
                      ...formData.requisitos_indispensables,
                    ]
                    updatedRequisitos[index] = event.target.value
                    updateFormData({
                      requisitos_indispensables: updatedRequisitos,
                    })
                  }}
                  rows={3}
                />
                {hasAttemptedSubmission && errors[`requisitos.${index}`] && (
                  <Text className="text-red-600 text-sm mt-1">
                    {errors[`requisitos.${index}`]}
                  </Text>
                )}
              </Box>
              {formData.requisitos_indispensables.length > 1 && (
                <Button
                  kind="tertiary"
                  size="small"
                  onClick={() =>
                    removeArrayItem('requisitos_indispensables', index)
                  }
                  aria-label="Eliminar requisito"
                >
                  <Remove />
                </Button>
              )}
            </Flex>
          ))}

          <Button
            kind="tertiary"
            size="small"
            onClick={() => addArrayItem('requisitos_indispensables', '')}
          >
            <Add /> Agregar Requisito
          </Button>
        </Box>
      </Box>

      {/* Action buttons */}
      <Flex justify="end" gap="small" className="mt-8 pt-6 border-t">
        <Button kind="tertiary" onClick={onCancel} disabled={isSubmitting}>
          Cancelar
        </Button>

        <Button
          onClick={handleSubmit}
          disabled={isSubmitting}
          loading={isSubmitting}
        >
          {mode === 'create' ? 'Crear Definición' : 'Actualizar Definición'}
        </Button>
      </Flex>
    </Box>
  )
}

export default JobDefinitionForm
